import { useCallback, useEffect, useState } from 'react';
import { FaFilter, FaPlus, FaStar } from "react-icons/fa";
import SummaryApi from '../common';
import productCategory from '../helpers/productCategory';
import { request } from '../helpers/request';

const FilterSidebar = ({ filters, onFilterChange, onClearFilters, className = "" }) => {
    const [allBrands, setAllBrands] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [showAllBrands, setShowAllBrands] = useState(false);

    const fetchCategories = useCallback(async () => {
        setLoadingCategories(true);
        try {
            const response = await request(SummaryApi.getAllCategories);
            const result = await response.json();
            if (result.success) {
                const uniqueCategories = [...productCategory];
                result.data.forEach(bc => {
                    if (!uniqueCategories.find(lc => lc.value === bc.name)) {
                        uniqueCategories.push({ label: bc.name, value: bc.name });
                    }
                });
                setAllCategories(uniqueCategories);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        } finally {
            setLoadingCategories(false);
        }
    }, []);

    const fetchBrands = useCallback(async () => {
        setLoadingBrands(true);
        try {
            const response = await request(SummaryApi.getAllBrands, {
                body: JSON.stringify({ category: filters.category || [] })
            });
            const result = await response.json();
            if (result.success) {
                setAllBrands(result.data);
            }
        } catch (error) {
            console.error("Error fetching brands:", error);
        } finally {
            setLoadingBrands(false);
        }
    }, [filters.category]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const handleCheckboxChange = (type, value, checked) => {
        const currentList = filters[type] || [];
        let newList;
        if (checked) {
            newList = [...currentList, value];
        } else {
            newList = currentList.filter(item => item !== value);
        }
        onFilterChange(type, newList);
    };

    return (
        <div className={`bg-white rounded-card shadow-premium border border-surface-border p-8 ${className}`}>
            <div className='flex items-center justify-between mb-8'>
                <h3 className='text-lg font-black text-primary tracking-tight flex items-center gap-2'>
                    <FaFilter className='text-xs text-accent' /> Filters
                </h3>
                <button onClick={onClearFilters} className='text-xs font-bold text-accent hover:underline'>Clear All</button>
            </div>

            <div className='space-y-8'>
                {/* Sort Section (Optional inside sidebar or kept outside) */}
                <div>
                    <h4 className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4'>Sort Results</h4>
                    <select 
                        value={filters.sort || "newest"} 
                        onChange={(e) => onFilterChange('sort', e.target.value)}
                        className='w-full h-10 px-4 bg-surface-muted border border-surface-border rounded-xl font-bold text-xs outline-none'
                    >
                        <option value="newest">Newest First</option>
                        <option value="price-asc">Price: Low to High</option>
                        <option value="price-desc">Price: High to Low</option>
                        <option value="rating">Top Rated</option>
                    </select>
                </div>

                {/* Price Range */}
                <div>
                    <h4 className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4'>Price Range</h4>
                    <div className='flex items-center gap-2'>
                        <input 
                            type="number" 
                            placeholder="Min" 
                            value={filters.minPrice || ""}
                            onChange={(e) => onFilterChange('minPrice', e.target.value)}
                            className='w-full h-10 px-3 bg-surface-muted border border-surface-border rounded-xl text-xs font-bold outline-none'
                        />
                        <span className='text-gray-300'>-</span>
                        <input 
                            type="number" 
                            placeholder="Max" 
                            value={filters.maxPrice || ""}
                            onChange={(e) => onFilterChange('maxPrice', e.target.value)}
                            className='w-full h-10 px-3 bg-surface-muted border border-surface-border rounded-xl text-xs font-bold outline-none'
                        />
                    </div>
                </div>

                {/* Categories */}
                <div>
                    <h4 className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4'>Category</h4>
                    <div className='flex flex-col gap-3 max-h-48 overflow-y-auto custom-scrollbar pr-2'>
                        {loadingCategories ? (
                            <div className='text-[10px] text-gray-400 animate-pulse'>Loading...</div>
                        ) : allCategories.map((category) => (
                            <label key={category.value} className='group flex items-center gap-3 cursor-pointer'>
                                <div className='relative flex items-center justify-center'>
                                    <input 
                                        type='checkbox' 
                                        checked={(filters.category || []).includes(category.value)} 
                                        onChange={(e) => handleCheckboxChange('category', category.value, e.target.checked)}
                                        className='peer appearance-none w-4 h-4 border-2 border-surface-border rounded checked:bg-accent checked:border-accent transition-all'
                                    />
                                    <FaPlus className='absolute text-white text-[8px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none' />
                                </div>
                                <span className='text-xs font-bold text-gray-600 group-hover:text-primary transition-colors'>{category.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Brands */}
                <div>
                    <h4 className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4'>Brand</h4>
                    <div className='flex flex-col gap-3 max-h-48 overflow-y-auto custom-scrollbar pr-2'>
                        {loadingBrands ? (
                            <div className='text-[10px] text-gray-400 animate-pulse'>Loading...</div>
                        ) : (
                            <>
                                {(showAllBrands ? allBrands : allBrands.slice(0, 8)).map((brand) => (
                                    <label key={brand} className='group flex items-center gap-3 cursor-pointer'>
                                        <div className='relative flex items-center justify-center'>
                                            <input 
                                                type='checkbox' 
                                                checked={(filters.brand || []).includes(brand)} 
                                                onChange={(e) => handleCheckboxChange('brand', brand, e.target.checked)}
                                                className='peer appearance-none w-4 h-4 border-2 border-surface-border rounded checked:bg-accent checked:border-accent transition-all'
                                            />
                                            <FaPlus className='absolute text-white text-[8px] opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none' />
                                        </div>
                                        <span className='text-xs font-bold text-gray-600 group-hover:text-primary transition-colors'>{brand}</span>
                                    </label>
                                ))}
                                {allBrands.length > 8 && (
                                    <button 
                                        onClick={() => setShowAllBrands(!showAllBrands)} 
                                        className='text-[10px] font-black uppercase text-accent hover:underline text-left mt-1 tracking-wider'
                                    >
                                        {showAllBrands ? 'Show Less' : `+${allBrands.length - 8} More`}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Rating Filter */}
                <div>
                    <h4 className='text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4'>Min Rating</h4>
                    <div className='flex gap-2'>
                        {[4, 3, 2, 1].map((rating) => (
                            <button
                                key={rating}
                                onClick={() => onFilterChange('minRating', filters.minRating === rating ? null : rating)}
                                className={`flex-1 py-2 rounded-lg border-2 text-[10px] font-black transition-all flex items-center justify-center gap-1
                                    ${filters.minRating === rating 
                                        ? 'border-yellow-400 bg-yellow-50 text-yellow-600' 
                                        : 'border-surface-border bg-white text-gray-400 hover:border-gray-300'
                                    }`}
                            >
                                {rating} <FaStar className="size-[8px]" />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSidebar;
